import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { banHangListParamsFromQueryString } from '@/utils/banHangListQueryUtils'
import { normalizeDetailResponse, normalizeListResponse, type PagedListResult } from '@/utils/apiResponseUtils'

const PREFIX = '/bao-cao'

export type BaoCaoTongQuan = {
  tongHangHoa: number
  tongKhachHang: number
  tongNcc: number
  tongDonHang: number
  tongHoaDon: number
  tongPhieuTra: number
  tongPhieuNhap: number
  tongPhieuKiem: number
  tongTonKho: number
  tongGiaVon: number
}

export type BaoCaoDoanhThuTheoNgay = {
  tuNgay: string
  denNgay: string
  tongHoaDon: number
  tongDoanhThu: number
  tongDaThu: number
  tongConNo?: number
  daily: Array<{ date: string; count: number; revenue: number; paid: number }>
}

export type CongNoKhachHangRow = {
  khachHangId: string
  maKhachHang: string
  tenKhachHang: string
  dienThoai?: string | null
  /** Username NVKD phụ trách. */
  maNhanVienPhuTrach?: string
  tenNhanVienPhuTrach?: string
  /** Có `tuNgay`: nợ trước kỳ; không lọc = 0. */
  noDauKy?: number
  phaiThuHoaDon?: number
  daThuHoaDon?: number
  phaiThu: number
  daThu: number
  tongThanhToan?: number
  tongKhauTru?: number
  tongChiPhiKhac?: number
  tongTamUngThue?: number
  tongKhoiTao?: number
  tongCanBang?: number
  tongHoanTien?: number
  conNo: number
  /** Cấu hình trên khách hàng — null = không giới hạn. */
  dinhMucCongNo?: number | null
  /** `YYYY-MM-DD` */
  ngayCanDoiNo?: string | null
  /** 1–31 */
  ngayDoiNoHangThang?: number | null
  /** Hạn đòi nợ áp dụng tại ngày tính `YYYY-MM-DD` (kiểu hàng tháng = ngày N của tháng đó). */
  hanDoiNo?: string | null
  /** Số ngày còn tới hạn đòi nợ; âm = đã quá hạn. */
  soNgayConLai?: number | null
  vuotDinhMuc?: boolean
  quaHanDoiNo?: boolean
  canDoiNo?: boolean
}

export type CongNoNhaCungCapRow = {
  nhaCungCapId: string
  maNhaCungCap: string
  tenNhaCungCap: string
  dienThoai?: string | null
  conNo: number
}

export type CongNoTheoDonRow = {
  loaiChungTu: 'HOA_DON' | 'TRA_HANG'
  id: string
  maChungTu: string
  /** Dòng phiếu trả: hóa đơn gốc (có thể null). */
  hoaDonId: string | null
  maHoaDon: string | null
  phieuTraHangId: string | null
  maPhieuTraHang: string | null
  thoiGian: string
  khachHangId: string | null
  maKhachHang: string
  tenKhachHang: string
  dienThoai?: string | null
  chiNhanhId?: number | null
  maChiNhanh?: string | null
  tenChiNhanh?: string | null
  maNhanVienPhuTrach?: string
  tenNhanVienPhuTrach?: string
  phaiThu: number
  daThu: number
  /** Dòng phiếu trả hàng: `canTraKhach`; dòng hóa đơn: 0. */
  tongHoanTien: number
  /** Dòng phiếu trả hàng: âm (giảm nợ). */
  conNo: number
}

export type CongNoTimelineRowKind = 'HOA_DON' | 'THANH_TOAN' | 'HOAN_TIEN' | 'GIAO_DICH'

export type CongNoTimelineRow = {
  id: string
  rowKind: CongNoTimelineRowKind
  thoiGian: string
  ma: string
  dienGiai: string
  hoaDonId: string | null
  maHoaDon: string
  phieuTraHangId: string | null
  phuongThuc: string | null
  ghiChu?: string | null
  /** Chi nhánh của hóa đơn (dòng HOA_DON / THANH_TOAN gắn HĐ). */
  chiNhanhId?: number | null
  maChiNhanh?: string | null
  tenChiNhanh?: string | null
  /** NVKD phụ trách trên khách hàng (giống list công nợ theo đơn). */
  maNhanVienPhuTrach?: string | null
  tenNhanVienPhuTrach?: string | null
  phaiThu: number
  daThu: number
  hoanTien: number
  /** Chỉ dòng HOA_DON: đã thu / trả hàng / còn nợ của riêng hóa đơn. */
  daThuHoaDon?: number
  hoanTienHoaDon?: number
  conLaiHoaDon?: number
  soDuLuyKe: number
  loaiGiaoDich?: string | null
  phanLoaiKhauTru?: string | null
  khoaSua?: boolean
  hinhAnhs?: string | null
  canHoanTienChoKhach?: boolean
  soTienNhan?: number | null
  soTienThuHo?: number | null
  thuHoCho?: string | null
  soTienCanHoan?: number | null
  ghiChuHoanTien?: string | null
  hinhAnhHoanTiens?: string | null
  trangThaiHoanTien?: string | null
  congTyNhanTien?: string | null
  congTyChuyenTien?: string | null
  taiKhoanQuyId?: string | null
  phieuThuId?: string | null
}

export type CongNoKhachHangDetail = {
  khachHangId: string
  maKhachHang: string
  tenKhachHang: string
  dienThoai?: string | null
  phaiThu: number
  daThu: number
  hoanTien: number
  tongThanhToan?: number
  tongKhauTru?: number
  tongChiPhiKhac?: number
  tongTamUngThue?: number
  tongKhoiTao?: number
  tongCanBang?: number
  tongHoanTien?: number
  conNo: number
  transactions: CongNoTimelineRow[]
}

export const fetchBaoCaoTongQuan = (): Promise<BaoCaoTongQuan> =>
  apiRequest(
    ApisServiceApp.get(`${PREFIX}/tong-quan`).then((res: unknown) =>
      normalizeDetailResponse(res as { data?: BaoCaoTongQuan }),
    ),
  )

export const fetchDoanhThuTheoNgay = (params?: {
  thang?: string
  tuNgay?: string
  denNgay?: string
}): Promise<BaoCaoDoanhThuTheoNgay> =>
  apiRequest(
    ApisServiceApp.get(`${PREFIX}/doanh-thu-theo-ngay`, { params: params ?? {} }).then((res: unknown) =>
      normalizeDetailResponse(res as { data?: BaoCaoDoanhThuTheoNgay }),
    ),
  )

export const fetchCongNoKhachHang = (search?: string): Promise<PagedListResult<CongNoKhachHangRow>> => {
  const params = search ? banHangListParamsFromQueryString(search) : { page: 1, pageSize: 25 }
  /** Công nợ KH = toàn dự án; bỏ chi nhánh nếu còn trên URL cũ. */
  delete (params as Record<string, unknown>).chiNhanhIds
  delete (params as Record<string, unknown>).chiNhanhId
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/cong-no-khach-hang`, { params }).then((res: unknown) => {
      const { data, paging, summary } = normalizeListResponse(
        res as Parameters<typeof normalizeListResponse>[0],
      )
      return { data: data as CongNoKhachHangRow[], paging, ...(summary ? { summary } : {}) }
    }),
  )
}

export const fetchCongNoNhaCungCap = (search?: string): Promise<PagedListResult<CongNoNhaCungCapRow>> => {
  const params = search ? banHangListParamsFromQueryString(search) : { page: 1, pageSize: 25 }
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/cong-no-nha-cung-cap`, { params }).then((res: unknown) => {
      const { data, paging } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return { data: data as CongNoNhaCungCapRow[], paging }
    }),
  )
}

export const fetchCongNoTheoDon = (search?: string): Promise<PagedListResult<CongNoTheoDonRow>> => {
  const params = search ? banHangListParamsFromQueryString(search) : { page: 1, pageSize: 25 }
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/cong-no-theo-don`, { params }).then((res: unknown) => {
      const { data, paging, summary } = normalizeListResponse(
        res as Parameters<typeof normalizeListResponse>[0],
      )
      return { data: data as CongNoTheoDonRow[], paging, ...(summary ? { summary } : {}) }
    }),
  )
}

export type CongNoTheoDonChiTietRow = {
  id: string
  loaiChungTu: 'HOA_DON' | 'TRA_HANG'
  chungTuId: string
  maChungTu: string
  thoiGian: string
  khachHangId: string | null
  maKhachHang: string
  tenKhachHang: string
  chiNhanhId?: number | null
  maChiNhanh?: string | null
  tenChiNhanh?: string | null
  maNhanVienPhuTrach?: string
  tenNhanVienPhuTrach?: string
  hangHoaId: string
  maHangHoa: string
  tenHangHoa: string
  donViTinh: string | null
  soLuong: number
  donGia: number
  giamGia: number
  thanhTien: number
  /** Giảm giá phiếu phân bổ về dòng (chỉ hóa đơn). */
  giamGiaPhieu: number
  thuKhac: number
  thue: number
  /** Dòng hóa đơn: dương (khách nợ thêm); dòng trả hàng: âm (giảm nợ). */
  congNo: number
}

export const fetchCongNoTheoDonChiTiet = (search?: string): Promise<PagedListResult<CongNoTheoDonChiTietRow>> => {
  const params = search ? banHangListParamsFromQueryString(search) : { page: 1, pageSize: 25 }
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/cong-no-theo-don-chi-tiet`, { params }).then((res: unknown) => {
      const { data, paging, summary } = normalizeListResponse(
        res as Parameters<typeof normalizeListResponse>[0],
      )
      return { data: data as CongNoTheoDonChiTietRow[], paging, ...(summary ? { summary } : {}) }
    }),
  )
}

export const fetchCongNoKhachHangDetail = (
  khachHangId: string,
  query?: { tuNgay?: string; denNgay?: string },
): Promise<CongNoKhachHangDetail> => {
  const params: Record<string, string> = {}
  if (query?.tuNgay) params.tuNgay = query.tuNgay
  if (query?.denNgay) params.denNgay = query.denNgay
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${encodeURIComponent(khachHangId)}/chi-tiet-bao-cao-cong-no-khach-hang`, {
      params,
    }).then((res: unknown) => normalizeDetailResponse(res as { data?: CongNoKhachHangDetail })),
  )
}

export type BaoCaoBangCol = {
  key: string
  label: string
  align?: 'left' | 'right'
  /** Tiêu đề nhóm nhiều tầng phía trên `label`. */
  group?: string[]
}

export type BaoCaoBangResponse = {
  title: string
  tuNgay: string
  denNgay: string
  columns: BaoCaoBangCol[]
  rows: Record<string, string | number | null>[]
  totals: Record<string, number>
  truncated?: boolean
  /** Có phân trang: tổng số dòng toàn bộ dữ liệu (`rows` chỉ là trang đang xem). */
  tongDong?: number
  /** Diễn giải thêm (vd. khoảng ngày từng kỳ của báo cáo so sánh). */
  ghiChu?: string
  /** Báo cáo so sánh: các kỳ theo thứ tự cột (id khớp hậu tố cột dt0, ln1…). */
  kySoSanh?: BaoCaoKySoSanh[]
}

export type BaoCaoKySoSanh = {
  id: number
  ten: string
  nhan: string
  tuNgay: string
  denNgay: string
  /** Kỳ chỉ để tham chiếu (không tính % tăng trưởng). */
  thamChieu?: boolean
}

/** Tham số báo cáo so sánh kỳ (SS_*); báo cáo PV_* / MG_* / LN_THANG chỉ dùng chi nhánh. */
export type BaoCaoSoSanhParams = {
  kieuKy?: string
  nam?: string
  quy?: string
  thang?: string
  soThang?: string
  /** Tuần ISO (kieuKy=TUAN). */
  tuan?: string
  /** kieuKy=TUY_CHON: CSV kỳ (`2026-10`, `2026-Q4`, `2026-W41`, `2026`, `2026-10-01~2026-10-15`), kỳ đầu là gốc. */
  cacKy?: string
  /** CSV id chi nhánh; trống = tất cả chi nhánh. */
  chiNhanhIds?: string
  /** CSV id dự án (báo cáo doanh thu – LN); trống = dự án đang đứng. */
  duAnIds?: string
  tachChiNhanh?: boolean
  /** Top sale: DOANH_THU | SO_LUONG | LOI_NHUAN. */
  tieuChi?: string
  /** Top sale: số sản phẩm mỗi nhóm xếp hạng. */
  soTop?: string
  /** Top sale: KY (cả kỳ) | THANG (từng tháng). */
  kieuXep?: string
}

/**
 * Một request báo cáo dạng thuần (loại + query string). Dùng để gọi API, để server
 * chạy lại khi lưu bản chụp, và làm khóa tra dữ liệu khi xem bản chụp.
 */
export type BaoCaoYeuCau = { loai: string; params: Record<string, string> }

const donParams = (raw: Record<string, string | number | boolean | null | undefined>): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(raw)) {
    if (v == null || v === '' || v === false) continue
    out[k] = String(v)
  }
  return out
}

/** Khóa ổn định của request: không phụ thuộc thứ tự tham số. */
export const khoaYeuCauBaoCao = (yc: BaoCaoYeuCau): string =>
  `${yc.loai}?${Object.keys(yc.params)
    .sort()
    .map((k) => `${k}=${yc.params[k]}`)
    .join('&')}`

/** Báo cáo pivot/tồn theo ngày × nhiều mảng có thể > 60s — nới riêng, không đổi timeout API chung. */
const BAO_CAO_KIOT_TIMEOUT_MS = 300_000

export const fetchYeuCauBaoCao = (yc: BaoCaoYeuCau): Promise<BaoCaoBangResponse> =>
  apiRequest(
    ApisServiceApp.get(`${PREFIX}/kiot/${encodeURIComponent(yc.loai)}`, {
      params: yc.params,
      timeout: BAO_CAO_KIOT_TIMEOUT_MS,
    }).then((res: unknown) => normalizeDetailResponse(res as { data?: BaoCaoBangResponse })),
  )

export type BaoCaoBangParams = {
  loai: string
  tuNgay?: string
  denNgay?: string
  moiQuanTam?: string
  /** Ô tìm chung — báo cáo kiot tìm trên mã chứng từ / mã, tên hàng / khách. */
  search?: string
  maGiaoDich?: string
  maHangHoa?: string
  tenHangHoa?: string
  soSanh?: BaoCaoSoSanhParams
  /** Phân trang (CHI_TIET | SO_SANH_CT): trang từ 1 + số dòng / trang; response có `tongDong`. */
  trang?: number
  soDong?: number
}

export const yeuCauBaoCaoBang = (params: BaoCaoBangParams): BaoCaoYeuCau => ({
  loai: params.loai,
  params: donParams({
    tuNgay: params.tuNgay,
    denNgay: params.denNgay,
    moiQuanTam: params.moiQuanTam,
    trang: params.trang,
    soDong: params.soDong,
    search: params.search,
    maGiaoDich: params.maGiaoDich,
    maHangHoa: params.maHangHoa,
    tenHangHoa: params.tenHangHoa,
    ...(params.soSanh
      ? {
          kieuKy: params.soSanh.kieuKy,
          nam: params.soSanh.nam,
          quy: params.soSanh.quy,
          thang: params.soSanh.thang,
          soThang: params.soSanh.soThang,
          tuan: params.soSanh.tuan,
          cacKy: params.soSanh.cacKy,
          chiNhanhIds: params.soSanh.chiNhanhIds,
          duAnIds: params.soSanh.duAnIds,
          tachChiNhanh: params.soSanh.tachChiNhanh ? 'true' : undefined,
          tieuChi: params.soSanh.tieuChi,
          soTop: params.soSanh.soTop,
          kieuXep: params.soSanh.kieuXep,
        }
      : {}),
  }),
})

export const fetchBaoCaoBang = (params: BaoCaoBangParams): Promise<BaoCaoBangResponse> =>
  fetchYeuCauBaoCao(yeuCauBaoCaoBang(params))

export type BaoCaoPivotParams = {
  loai: 'doanh-thu-ln' | 'ton-kho'
  /** Mặc định PIVOT; SO_SANH = pivot so sánh kỳ (gửi kèm `soSanh`, bỏ qua tuNgay / denNgay). */
  moiQuanTam?: string
  /** Pivot tồn kho: TON | GIAO_DICH | GOI_Y. */
  nguon?: string
  /** Kỳ so sánh (moiQuanTam=SO_SANH). */
  soSanh?: Pick<BaoCaoSoSanhParams, 'kieuKy' | 'nam' | 'quy' | 'thang' | 'soThang' | 'tuan' | 'cacKy'>
  tuNgay: string
  denNgay: string
  /** CSV trường server cần gom (rỗng = chỉ gom theo kỳ). */
  truong: string
  /** NGAY | THANG | NAM | KY — mỗi dòng có `ky` = ngày đầu kỳ, KY = gộp cả khoảng ngày. */
  grain: string
  duAnIds?: string
  chiNhanhIds?: string
  search?: string
  maGiaoDich?: string
  maHangHoa?: string
  tenHangHoa?: string
}

/** Dữ liệu phẳng cho pivot: đã gom theo `truong` × kỳ `grain`, FE tự dựng dòng / cột. */
export const yeuCauBaoCaoPivot = (params: BaoCaoPivotParams): BaoCaoYeuCau => ({
  loai: params.loai,
  params: {
    ...donParams({
      moiQuanTam: params.moiQuanTam || 'PIVOT',
      nguon: params.nguon,
      ...(params.soSanh
        ? {
            kieuKy: params.soSanh.kieuKy,
            nam: params.soSanh.nam,
            quy: params.soSanh.quy,
            thang: params.soSanh.thang,
            soThang: params.soSanh.soThang,
            tuan: params.soSanh.tuan,
            cacKy: params.soSanh.cacKy,
          }
        : { tuNgay: params.tuNgay, denNgay: params.denNgay }),
      grain: params.grain,
      duAnIds: params.duAnIds,
      chiNhanhIds: params.chiNhanhIds,
      search: params.search,
      maGiaoDich: params.maGiaoDich,
      maHangHoa: params.maHangHoa,
      tenHangHoa: params.tenHangHoa,
    }),
    /** Rỗng = chỉ gom theo kỳ; bỏ hẳn tham số thì server dùng trường mặc định. */
    truong: params.truong,
  },
})

export const fetchBaoCaoPivot = (params: BaoCaoPivotParams): Promise<BaoCaoBangResponse> =>
  fetchYeuCauBaoCao(yeuCauBaoCaoPivot(params))

export type BaoCaoPhamViDuAn = {
  id: number
  ma: string
  ten: string
  chiNhanhs: { id: number; ma: string; ten: string }[]
}

/** Mọi dự án (mảng) kèm chi nhánh — chỉ gọi khi có quyền xem báo cáo toàn hệ thống. */
export const fetchPhamViBaoCao = (): Promise<BaoCaoPhamViDuAn[]> =>
  apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-pham-vi-bao-cao`).then((res: unknown) => {
      const raw = normalizeDetailResponse<unknown>(res as unknown[] | { data?: unknown })
      return Array.isArray(raw) ? (raw as BaoCaoPhamViDuAn[]) : []
    }),
  )
